<script setup>
import { ref, computed, onMounted} from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ start: null, end: null })
  },
  disabled: {
    type: Boolean,
    default: false
  },
  selectedQuickRangeLabel: {
    type: String,
    default: null
  }
})

const showModal = ref(false)
const buttonRef = ref(null)
const startDate = ref(null)
const endDate = ref(null)
const hoverDate = ref(null)
const modalRef = ref(null)
const rangeDate = ref({ start: null, end: null })
const quickRangeLabel = ref(null)
const appliedQuickRangeLabel = ref(null)
const emit = defineEmits(['rangeDate', 'clear', 'update:selectedQuickRangeLabel'])


const currentMonth1 = ref(new Date())
const currentMonth2 = ref(new Date(new Date().getFullYear(), new Date().getMonth() + 1))

const today = new Date()
today.setHours(0, 0, 0, 0)

const openModal = () => {
  if (props.disabled) return
  showModal.value = true
  // Initialize with existing values if available
  if (showModal.value && props.modelValue?.start && props.modelValue?.end) {
    startDate.value = new Date(props.modelValue.start)
    endDate.value = new Date(props.modelValue.end)
  }
  // Restore quick range label from applied state
  quickRangeLabel.value = appliedQuickRangeLabel.value
}

const clearDateRange = () => {
  startDate.value = null
  endDate.value = null
  rangeDate.value = { start: null, end: null }
  appliedQuickRangeLabel.value = null
  emit('rangeDate', rangeDate.value)
  emit('update:selectedQuickRangeLabel', null)
  emit('clear')
  closeModal()
}

const handleClickOutside = (event) => {
  if (showModal.value && 
      modalRef.value && 
      !modalRef.value.contains(event.target) && 
      buttonRef.value && 
      !buttonRef.value.contains(event.target)) {
    closeModal()
  }
}

const closeModal = () => {
  showModal.value = false
  hoverDate.value = null
  quickRangeLabel.value = null
}

const applyDateRange = () => {
  if (startDate.value && endDate.value) {
    rangeDate.value = {
      start: startDate.value,
      end: endDate.value
    };
    appliedQuickRangeLabel.value = quickRangeLabel.value
    emit('rangeDate', rangeDate.value)
    emit('update:selectedQuickRangeLabel', quickRangeLabel.value)
    showModal.value = false
    hoverDate.value = null
  }
}

const getDaysInMonth = (date) => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInPrevMonth = new Date(year, month, 0).getDate()

  const days = [];

  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;

  for (let i = adjustedFirstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    const dayDate = new Date(year, month - 1, day);
    days.push({ 
      day, 
      date: dayDate,
      isCurrentMonth: false,
      isDisabled: true
    });
  }

  for (let i = 1; i <= daysInMonth; i++) {
    const dayDate = new Date(year, month, i);
    const isPast = dayDate < today;
    days.push({ 
      day: i, 
      date: dayDate,
      isCurrentMonth: true,
      isDisabled: isPast
    });
  }

  const remainingDays = 35 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const dayDate = new Date(year, month + 1, i)
    days.push({ 
      day: i, 
      date: dayDate,
      isCurrentMonth: false,
      isDisabled: true
    });
  }

  return days;
};

const days1 = computed(() => getDaysInMonth(currentMonth1.value))
const days2 = computed(() => getDaysInMonth(currentMonth2.value))

const getMonthName = (date) => {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const previousMonth = () => {
  currentMonth1.value = new Date(currentMonth1.value.getFullYear(), currentMonth1.value.getMonth() - 1)
  currentMonth2.value = new Date(currentMonth1.value.getFullYear(), currentMonth1.value.getMonth() + 1)
};

const nextMonth = () => {
  currentMonth1.value = new Date(currentMonth1.value.getFullYear(), currentMonth1.value.getMonth() + 1)
  currentMonth2.value = new Date(currentMonth1.value.getFullYear(), currentMonth1.value.getMonth() + 1)
};

const previousYear = () => {
  currentMonth1.value = new Date(currentMonth1.value.getFullYear() - 1, currentMonth1.value.getMonth())
  currentMonth2.value = new Date(currentMonth1.value.getFullYear(), currentMonth1.value.getMonth() + 1)
};

const nextYear = () => {
  currentMonth1.value = new Date(currentMonth1.value.getFullYear() + 1, currentMonth1.value.getMonth())
  currentMonth2.value = new Date(currentMonth1.value.getFullYear(), currentMonth1.value.getMonth() + 1)
};

const selectDate = (day) => {
  if (day.isDisabled) return

  // Limpiar el label de selección rápida cuando se selecciona manualmente
  quickRangeLabel.value = null

  if (!startDate.value || (startDate.value && endDate.value)) {
    startDate.value = day.date
    endDate.value = null
  } else if (day.date < startDate.value) {
    endDate.value = startDate.value
    startDate.value = day.date
  } else {
    endDate.value = day.date
  }
}


const onMouseEnter = (day) => {
  if (startDate.value && !endDate.value && !day.isDisabled) {
    hoverDate.value = day.date
  }
};

const onMouseLeave = () => {
  hoverDate.value = null
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

const isToday = (day) => {
  if (!day.isCurrentMonth) return false
  return day.date.getTime() === today.getTime()
};

const isSelected = (day) => {
  if (!day.date) return false
  const time = day.date.getTime()
  
  if (startDate.value && startDate.value.getTime() === time) return true
  if (endDate.value && endDate.value.getTime() === time) return true
  
  return false
};

const isInRange = (day) => {
  if (!day.date || !startDate.value) return false
  
  const time = day.date.getTime();
  const start = startDate.value.getTime()
  
  if (endDate.value) {
    const end = endDate.value.getTime()
    return time > start && time < end
  } else if (hoverDate.value) {
    const hover = hoverDate.value.getTime()
    if (hover > start) {
      return time > start && time < hover
    }
  }
  
  return false
};

const isRangeStart = (day) => {
  if (!day.date || !startDate.value) return false
  return day.date.getTime() === startDate.value.getTime()
};

const isRangeEnd = (day) => {
  if (!day.date || !endDate.value) return false
  return day.date.getTime() === endDate.value.getTime()
};

const selectQuickRange = (days) => {
  const start = new Date(today)
  const end = new Date(today)
  end.setDate(end.getDate() + days)
  
  startDate.value = start
  endDate.value = end
  
  // Guardar el label del botón de selección rápida
  quickRangeLabel.value = `Next ${days} Days`
};

// Method exposed for external use - applies quick range
// When silent is true, only updates internal state without emitting (used for restoring state)
const applyQuickRange = (days, { silent = false } = {}) => {
  const quickOptions = [2, 5, 10, 15, 30, 60]
  if (!quickOptions.includes(days)) {
    return
  }
  
  const start = new Date(today)
  const end = new Date(today)
  end.setDate(end.getDate() + days)
  
  startDate.value = start
  endDate.value = end
  quickRangeLabel.value = `Next ${days} Days`
  appliedQuickRangeLabel.value = `Next ${days} Days`
  
  rangeDate.value = { start, end }
  if (!silent) {
    emit('rangeDate', rangeDate.value)
    emit('update:selectedQuickRangeLabel', quickRangeLabel.value)
  }
};

const formattedRange = computed(() => {
  if (!startDate.value || !endDate.value) return 'Your date selection will appear here.';
  
  const formatDate = (date, includeYear = true) => {
    const options = { 
      month: 'short', 
      day: 'numeric'
    };
    
    if (includeYear) {
      options.year = 'numeric'
    }
    
    return date.toLocaleDateString('en-US', options)
  };
  
  const startYear = startDate.value.getFullYear()
  const endYear = endDate.value.getFullYear()
  
  if (startYear !== endYear) {
    return `${formatDate(startDate.value, true)} - ${formatDate(endDate.value, true)}`
  } else {
    return `${formatDate(startDate.value, false)} - ${formatDate(endDate.value, true)}`
  }
});

const buttonLabel = computed(() => {
  if (!props.modelValue?.start || !props.modelValue?.end) return 'Select Date Range';
  
  // Use external prop label if provided (e.g., from 30-day filter action)
  if (props.selectedQuickRangeLabel) {
    return props.selectedQuickRangeLabel
  }
  
  // Use applied quick range label if available
  if (appliedQuickRangeLabel.value) {
    return appliedQuickRangeLabel.value
  }
  
  // Check if current range matches a quick select option
  const quickRangeMatch = detectQuickRangeMatch()
  if (quickRangeMatch) {
    return quickRangeMatch
  }
  
  const formatDate = (dateStr, includeYear = true) => {
    const date = new Date(dateStr);
    const options = { 
      month: 'short', 
      day: 'numeric'
    };
    
    if (includeYear) {
      options.year = 'numeric'
    }
    
    return date.toLocaleDateString('en-US', options)
  };
  
  const startYear = new Date(props.modelValue.start).getFullYear()
  const endYear = new Date(props.modelValue.end).getFullYear()
  
  if (startYear !== endYear) {
    return `${formatDate(props.modelValue.start, true)}–${formatDate(props.modelValue.end, true)}`
  } else {
    return `${formatDate(props.modelValue.start, false)}–${formatDate(props.modelValue.end, true)}`
  }
});

const detectQuickRangeMatch = () => {
  if (!props.modelValue?.start || !props.modelValue?.end) return null
  
  const start = new Date(props.modelValue.start)
  const end = new Date(props.modelValue.end)
  
  // Normalize dates to start of day for comparison
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  
  const todayNormalized = new Date(today)
  todayNormalized.setHours(0, 0, 0, 0)
  
  // Check if start is today
  if (start.getTime() !== todayNormalized.getTime()) return null
  
  // Calculate days difference
  const diffTime = end.getTime() - start.getTime()
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))
  
  // Check against quick select options
  const quickOptions = [2, 5, 10, 15, 30, 60]
  if (quickOptions.includes(diffDays)) {
    return `Next ${diffDays} Days`
  }
  
  return null
}

const canApply = computed(() => {
  return startDate.value && endDate.value;
})

const hasSelectedRange = computed(() => {
  return props.modelValue?.start && props.modelValue?.end
})

// Expose methods for parent components
defineExpose({
  applyQuickRange,
  clearDateRange
})
</script>

<template>
  <div class="date-picker-container" :class="{ 'is-disabled': disabled }">
    <button ref="buttonRef" @click="openModal" class="btn btn-outline-primary date-range-button" :class="{ 'active': showModal || hasSelectedRange }">
      {{ buttonLabel }}
      <i class="bi bi-calendar-week ms-1"></i>
    </button>

    <!-- Modal Dropdown -->
    <div v-if="showModal" ref="modalRef"  class="date-picker-modal">
      <div class="modal-arrow"></div>

      <div class="modal-content">
        <div class="d-flex">
          <!-- Calendarios personalizados -->
          <div class="calendars-container">
            <!-- Calendar 1 -->
            <div class="calendar">
              <div class="calendar-header">
                <button @click="previousYear" class="nav-btn">
                  <i class="bi bi-chevron-double-left"></i>
                </button>
                <button @click="previousMonth" class="nav-btn">
                  <i class="bi bi-chevron-left"></i>
                </button>
                <div class="month-label">{{ getMonthName(currentMonth1) }}</div>
                <button class="nav-btn invisible">
                  <i class="bi bi-chevron-right"></i>
                </button>
                <button class="nav-btn invisible">
                  <i class="bi bi-chevron-double-right"></i>
                </button>
              </div>

              <div class="calendar-grid">
                <div class="day-header">Mon</div>
                <div class="day-header">Tue</div>
                <div class="day-header">Wed</div>
                <div class="day-header">Thu</div>
                <div class="day-header">Fri</div>
                <div class="day-header">Sat</div>
                <div class="day-header">Sun</div>

                <div
                  v-for="(day, index) in days1"
                  :key="`cal1-${index}`"
                  class="day-cell"
                  :class="{
                    'inactive': !day.isCurrentMonth,
                    'disabled': day.isDisabled,
                    'today': isToday(day),
                    'selected': isSelected(day),
                    'in-range': isInRange(day),
                    'range-start': isRangeStart(day),
                    'range-end': isRangeEnd(day)
                  }"
                  @click="selectDate(day)"
                  @mouseenter="onMouseEnter(day)"
                  @mouseleave="onMouseLeave"
                >
                  {{ day.day }}
                </div>
              </div>
            </div>

            <!-- Calendar 2 -->
            <div class="calendar">
              <div class="calendar-header">
                <button class="nav-btn invisible">
                  <i class="bi bi-chevron-double-left"></i>
                </button>
                <button class="nav-btn invisible">
                  <i class="bi bi-chevron-left"></i>
                </button>
                <div class="month-label">{{ getMonthName(currentMonth2) }}</div>
                <button @click="nextMonth" class="nav-btn">
                  <i class="bi bi-chevron-right"></i>
                </button>
                <button @click="nextYear" class="nav-btn">
                  <i class="bi bi-chevron-double-right"></i>
                </button>
              </div>

              <div class="calendar-grid">
                <div class="day-header">Mon</div>
                <div class="day-header">Tue</div>
                <div class="day-header">Wed</div>
                <div class="day-header">Thu</div>
                <div class="day-header">Fri</div>
                <div class="day-header">Sat</div>
                <div class="day-header">Sun</div>

                <div
                  v-for="(day, index) in days2"
                  :key="`cal2-${index}`"
                  class="day-cell"
                  :class="{
                    'inactive': !day.isCurrentMonth,
                    'disabled': day.isDisabled,
                    'today': isToday(day),
                    'selected': isSelected(day),
                    'in-range': isInRange(day),
                    'range-start': isRangeStart(day),
                    'range-end': isRangeEnd(day)
                  }"
                  @click="selectDate(day)"
                  @mouseenter="onMouseEnter(day)"
                  @mouseleave="onMouseLeave"
                >
                  {{ day.day }}
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Select Buttons -->
          <div class="quick-select">
            <button @click="selectQuickRange(60)" class="btn btn-outline-primary quick-btn text-14" :class="{ 'quick-btn-active': quickRangeLabel === 'Next 60 Days' }">
              Next 60 Days
            </button>
            <button @click="selectQuickRange(30)" class="btn btn-outline-primary quick-btn text-14" :class="{ 'quick-btn-active': quickRangeLabel === 'Next 30 Days' }">
              Next 30 Days
            </button>
            <button @click="selectQuickRange(15)" class="btn btn-outline-primary quick-btn text-14" :class="{ 'quick-btn-active': quickRangeLabel === 'Next 15 Days' }">
              Next 15 Days
            </button>
            <button @click="selectQuickRange(10)" class="btn btn-outline-primary quick-btn text-14" :class="{ 'quick-btn-active': quickRangeLabel === 'Next 10 Days' }">
              Next 10 Days
            </button>
            <button @click="selectQuickRange(5)" class="btn btn-outline-primary quick-btn text-14" :class="{ 'quick-btn-active': quickRangeLabel === 'Next 5 Days' }">
              Next 5 Days
            </button>
            <button @click="selectQuickRange(2)" class="btn btn-outline-primary quick-btn text-14" :class="{ 'quick-btn-active': quickRangeLabel === 'Next 2 Days' }">
              Next 2 Days
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <div>
            <button @click="clearDateRange" class="btn btn-outline-primary">Clear</button>
          </div>
          <div class="text-end text-14 selection-text">You've selected {{ formattedRange }}</div>
          <div class="action-buttons">
            <button @click="closeModal" class="btn btn-outline-primary">Cancel</button>
            <button @click="applyDateRange" :disabled="!canApply" class="btn btn-primary">Apply</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.date-picker-container {
  position: relative;
}

.date-picker-modal {
  position: absolute;
  top: calc(100% + 35px);
  left: 0;
  background: var(--bs-gray-100);
  border-radius: 4px;
  padding: 10px;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--bs-gray-400);
}

.modal-arrow {
  position: absolute;
  top: -24px;
  left: 20px;
  width: 0;
  height: 0;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-bottom: 24px solid var(--bs-gray-100);
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.05));

  &::before {
    content: '';
    position: absolute;
    top: 1px;
    left: -15px;
    width: 0;
    height: 0;
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    border-bottom: 24px solid var(--bs-gray-100);
  }
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 17px;
}

.calendars-container {
  display: flex;
}

.calendar {
  border: 1px solid var(--bs-gray-400);
  background: #fff;
  border-radius: 4px;

  &:nth-child(2) {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 5px;
  border-bottom: 1px solid var(--bs-gray-400);
  padding: 8px;
}

.nav-btn {
  background: none;
  border: none;
  color: var(--bs-primary);
  font-size: 16px;
  cursor: pointer;
  padding: 5px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover:not(.invisible) {
    background-color: #f0e6ff;
    color: var(--bs-primary);
  }

  &.invisible {
    visibility: hidden;
  }
}

.month-label {
  color: var(--bs-primary);
  font-size: 14px;
  flex: 1;
  text-align: center;
  font-weight: 500;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  padding: 0 8px 8px 8px;
}

.day-header {
  text-align: center;
  padding: 8px 5px 0px 5px;
  font-size: 14px;
}

.day-cell {
  text-align: center;
  padding: 10px 5px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  min-width: 40px;
  position: relative;
  user-select: none;

  &:hover:not(.disabled):not(.inactive) {
    background-color: #f0e6ff;
  }

  &.inactive {
    color: #ccc;
    cursor: default;

    &:hover {
      background-color: transparent;
    }
  }

  &.disabled {
    color: #ccc;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }

  &.today {
    background-color: var(--bs-gray-300);
    border-radius: 50%;
    border: 1px solid var(--bs-gray-400);
  }

  &.in-range {
    background-color: #F7F1FD !important;
    border-radius: 0;
  }

  &.range-start,
  &.range-end {
    background-color: transparent !important;
    color: #fff !important;
    font-weight: 600;
    position: relative;
    z-index: 2;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 32px;
      height: 32px;
      background-color: var(--bs-primary);
      border-radius: 50%;
      z-index: -1;
    }
  }

  &.range-start:not(.range-end) {
    background: repeating-radial-gradient(#F7F1FD, transparent 100px);
    color: #fff !important;
    border: none!important;
    border-radius: 20px 0 0 20px;
    transition: all 0.2s ease;
  }

  &.range-end:not(.range-start) {
    border: none!important;
    background: repeating-radial-gradient(#F7F1FD, transparent 100px);
    color: #fff !important;
    border-radius: 0 20px 20px 0;
    transition: all 0.2s ease;

  }

 
}

.quick-select {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 10px;
  width: max-content;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid var(--bs-gray-400);
}

.selection-text {
  flex: 1;
  padding-right: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.quick-btn {
  background-color: #fff;
  white-space: nowrap; 
  &.quick-btn-active {
    background-color: var(--bs-primary);
    color: #fff;
    border-color: var(--bs-primary);
  }
}

.date-range-button.active {
  background-color: var(--bs-primary);
  color: #fff;
  border-color: var(--bs-primary);
}

.is-disabled {
  pointer-events: none;
  opacity: 0.65;
}
</style>